/**
 * A modal that displays a title and a message and can only be dismissed by
 * clicking the acknowledge button (no cancel, and backdrop click and Escape
 * do nothing). The page behind it is blurred, and the button is disabled for
 * the first few seconds so the message gets read. Use it when the user has to
 * see something before continuing, e.g. that exported data is for internal
 * use only.
 */

import Box from "@mui/material/Box";
import { ReactEventHandler, useEffect, useState } from "react";
import { Button } from "react-admin";

import { ActionButtonsBar, Modal } from "..";

const ACKNOWLEDGE_DELAY_SECONDS = 2;

interface MustAcknowledgeModalProps {
  acknowledgeHandler: ReactEventHandler<HTMLButtonElement>;
  message: string;
  modalOpen: boolean;
  title: string;
}

function MustAcknowledgeModal(props: MustAcknowledgeModalProps) {
  const [secondsLeft, setSecondsLeft] = useState(ACKNOWLEDGE_DELAY_SECONDS);

  // Restart the countdown every time the modal opens.
  useEffect(() => {
    if (!props.modalOpen) {
      return;
    }

    setSecondsLeft(ACKNOWLEDGE_DELAY_SECONDS);
    const interval = setInterval(() => {
      setSecondsLeft((seconds: number) => Math.max(seconds - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [props.modalOpen]);

  return (
    <Modal blurBackdrop open={props.modalOpen} title={props.title}>
      <Box
        component="p"
        sx={{ color: "fieldwire.gray.1", lineHeight: "150%", margin: "0" }}
      >
        {props.message}
      </Box>
      <ActionButtonsBar>
        <Button
          disabled={secondsLeft > 0}
          label={
            secondsLeft > 0 ? `Acknowledge (${secondsLeft})` : "Acknowledge"
          }
          onClick={props.acknowledgeHandler}
          variant="fieldwire-primary"
        />
      </ActionButtonsBar>
    </Modal>
  );
}

export default MustAcknowledgeModal;
